/**
 * Core-100 R4 — OD-06 (founder decision): customer reminders are shop-configurable and are DISPATCHED
 * only after the shop has verified its consent capture.
 *
 * The rule lives in the database (guard_reminder_dispatch_consent, migration 20270206013900):
 *   - dispatch cannot be switched on while consent capture is unverified;
 *   - the verification is stamped by the server as the signed-in manager, now (no back-dating);
 *   - withdrawing the verification switches dispatch off;
 *   - every change is in an append-only log.
 * This control only asks for those changes and shows the recorded state.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ShieldCheck } from 'lucide-react';

// The OD-06 columns are newer than the generated database types; read/write them through an untyped
// view of the SAME client (never a detached method).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const untyped = () => supabase as unknown as { from: (table: string) => any };

interface DispatchState {
  dispatch_enabled: boolean;
  consent_capture_verified_at: string | null;
}

export function ReminderDispatchControl({ shopId }: { shopId: string }) {
  const [state, setState] = useState<DispatchState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data, error: readError } = await untyped()
      .from('reminder_settings')
      .select('dispatch_enabled, consent_capture_verified_at')
      .eq('shop_id', shopId)
      .maybeSingle();
    if (readError) {
      setError('Could not load the reminder dispatch setting.');
      return;
    }
    setState((data as DispatchState | null) ?? { dispatch_enabled: false, consent_capture_verified_at: null });
  }, [shopId]);

  useEffect(() => { void load(); }, [load]);

  const write = async (patch: Partial<Record<keyof DispatchState, unknown>>) => {
    setBusy(true);
    setError(null);
    const { data, error: writeError } = await untyped()
      .from('reminder_settings')
      .update(patch)
      .eq('shop_id', shopId)
      .select('dispatch_enabled, consent_capture_verified_at');
    setBusy(false);
    if (writeError) {
      setError(writeError.message.includes('REMINDER_DISPATCH_REQUIRES_VERIFIED_CONSENT')
        ? 'Verify your consent capture before turning reminder sending on.'
        : 'The change was not saved.');
      return;
    }
    if (!data || data.length === 0) {
      setError('Only a manager can change reminder sending for this shop. Save the reminder settings first if none exist.');
      return;
    }
    await load();
  };

  if (!state) {
    return error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null;
  }
  const verified = !!state.consent_capture_verified_at;

  return (
    <div className="space-y-3" data-testid="reminder-dispatch-control">
      <div className="flex items-start gap-2">
        <ShieldCheck className="h-5 w-5 mt-0.5" />
        <div className="space-y-1">
          <Label className="text-base">Send reminders to customers</Label>
          <p className="text-sm text-muted-foreground">
            Reminders are only sent once your shop has confirmed that it records each customer&apos;s
            consent to receive them.
          </p>
        </div>
      </div>

      {verified ? (
        <p className="text-sm" data-testid="consent-verified">
          Consent capture verified on {new Date(state.consent_capture_verified_at as string).toLocaleDateString()}.{' '}
          <Button variant="link" className="h-auto p-0" disabled={busy}
            onClick={() => void write({ consent_capture_verified_at: null })}>
            Withdraw verification
          </Button>
        </p>
      ) : (
        <Alert>
          <AlertDescription className="space-y-2">
            <p data-testid="consent-unverified">
              Not verified yet — customer reminders stay queued and are not sent.
            </p>
            <Button size="sm" disabled={busy}
              onClick={() => void write({ consent_capture_verified_at: new Date().toISOString() })}>
              I have verified our consent capture
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <Label htmlFor="reminder-dispatch-enabled">Sending on</Label>
        <Switch
          id="reminder-dispatch-enabled"
          checked={state.dispatch_enabled}
          disabled={busy || !verified}
          onCheckedChange={(on) => void write({ dispatch_enabled: on })}
        />
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </div>
  );
}
